<template>
    <div class="marketing-landing-page">
        <div class="marketing-landing-page__content">
            <div class="marketing-landing-page__content--title">
                <h1 class="typographies-display-brand-large">
                    {{ $l10n.__('Décuplez votre potentiel de croissance') }}
                </h1>
                <ImgSvg class="underline" width="193px" height="9px">
                    <Underline />
                </ImgSvg>
            </div>

            <div class="marketing-landing-page__content--informations">
                <p
                    class="typographies-paragraph-large-regular"
                    v-html="
                        $t.__('marketing.landing.MarketingLandingPage.informations_line_1', {
                            startStrong: '<span class=\'typographies-paragraph-large-semibold\'>',
                            endStrong: '</span>',
                        })
                    "
                />

                <p
                    class="typographies-paragraph-large-regular"
                    v-html="
                        $t.__('marketing.landing.MarketingLandingPage.informations_line_2', {
                            startStrong: '<span class=\'typographies-paragraph-large-semibold\'>',
                            endStrong: '</span>',
                        })
                    "
                />

                <p
                    class="typographies-paragraph-large-regular"
                    v-html="
                        $t.__('marketing.landing.MarketingLandingPage.informations_line_3', {
                            startStrong: '<span class=\'typographies-paragraph-large-semibold\'>',
                            endStrong: '</span>',
                        })
                    "
                />
            </div>

            <div class="marketing-landing-page__content--buttons">
                <CompassButton
                    v-if="!state.isValidPricing"
                    :label="$t.__('common.actions.ask_demo')"
                    type="secondary"
                    size="large"
                    @click="redirectTo('https://go.sellsy.com/demande-de-rendez-vous', '_blank')"
                />
                <CompassTooltip
                    :content="tooltipConfigureButton"
                    :disabled="enableConfigureButton"
                    placement="bottom-start"
                >
                    <CompassButton
                        ref="configureButton"
                        :disabled="!enableConfigureButton"
                        :label="labelConfigureButton"
                        size="large"
                        @click="clickOnConfigureButton"
                    />
                </CompassTooltip>
            </div>
        </div>

        <div class="marketing-landing-page__illustration">
            <img src="/ressources/logged/img/illustrations/marketing-widget-illustration.png" />
        </div>

        <CompassDialog
            :visible.sync="displayCorpVerificationModal"
            :title="$t.__('marketing.landing.MarketingLandingPage.account_checking')"
            :primary-action="primaryActionCorpVerificationModal"
            :secondary-actions="secondaryActionsCorpVerificationModal"
            size="small"
        >
            <template #default>
                <span
                    class="typographies-paragraph-medium-regular"
                    v-html="
                        $t.__('marketing.landing.MarketingLandingPage.dialog.description', {
                            br: '<br />',
                            startStrong: '<span class=\'typographies-paragraph-medium-semibold\'>',
                            endStrong: '</span>',
                        })
                    "
                />
            </template>
        </CompassDialog>

        <preloader :display="isLoading" />
    </div>
</template>

<script>
import { goToUrl } from 'vueDir/helpers.js';
import ImgSvg from 'vcomponents/imgs/ImgSvg.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import Underline from 'vcomponents/imgs/ImgUnderlineUltraBlueMarine.vue';
import marketingService from 'vueDir/api/marketing/marketingService.js';

export default {
    components: {
        Preloader,
        Underline,
        ImgSvg,
    },
    props: {
        state: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            displayCorpVerificationModal: false,
            corpVerificationInProgress: this.state.isVerificationAccountPending ?? false,
            primaryActionCorpVerificationModal: {
                label: this.$l10n.__('Débuter la vérification dans le tchat'),
                action: this.sendAccountVerificationRequest,
            },
            secondaryActionsCorpVerificationModal: [
                {
                    label: this.$l10n.__('Annuler'),
                    action: () => {
                        this.displayCorpVerificationModal = false;
                    },
                },
            ],
        };
    },
    computed: {
        enableConfigureButton() {
            let canConfigure = false;

            if (this.state.canConfigure || this.state.isAccountVerified !== undefined) {
                canConfigure = true;
            }

            if (
                this.corpVerificationInProgress ||
                this.state.isVerificationAccountBlocked ||
                !this.state.isAccessGranted ||
                !this.state.isValidPricing ||
                !this.state?.isAdmin
            ) {
                canConfigure = false;
            }

            return canConfigure;
        },
        labelConfigureButton() {
            return this.corpVerificationInProgress
                ? this.$l10n.__('Accès en attente')
                : this.$l10n.__('Découvrir le module');
        },
        tooltipConfigureButton() {
            let contentTooltipConfigureButton = this.$l10n.__(
                'Seul un administrateur peut accéder à ce module',
            );

            if (this.corpVerificationInProgress) {
                contentTooltipConfigureButton = this.$l10n.__(
                    'Votre demande d’accès est en cours de traitement.',
                );
            } else if (this.state.isVerificationAccountBlocked) {
                contentTooltipConfigureButton = this.$l10n.__(
                    'Votre compte n’a pas pu être vérifié. Vous pouvez contactez nos équipes.',
                );
            }

            return contentTooltipConfigureButton;
        },
    },
    methods: {
        goToUrl,
        clickOnConfigureButton() {
            if (
                this.state.canConfigure ||
                (this.state.isAccountVerified &&
                    this.state.isAccessGranted &&
                    this.state.isValidPricing)
            ) {
                this.goToUrl('/marketing/configure');
            } else if (!this.state.isAccountVerified && !this.corpVerificationInProgress) {
                this.displayCorpVerificationModal = true;
            }
        },
        redirectTo(url, target) {
            window.open(url, target);
        },
        sendAccountVerificationRequest() {
            this.isLoading = true;
            this.displayCorpVerificationModal = false;
            marketingService
                .sendAccountVerificationRequest()
                .then(() => {
                    this.corpVerificationInProgress = true;
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__('Une erreur est survenue, merci de réessayer'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.marketing-landing-page {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 137px;

    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-6);
        width: 426px;

        &--title {
            h1 {
                color: var(--colors-common-text-brand-secondary-blue);
                margin: 0;
            }
        }

        &--informations {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-3);

            p {
                color: var(--colors-common-text-brand-secondary-blue);
                margin: 0;
                padding: 0 var(--spacing-1);

                &:not(:last-child) {
                    padding-bottom: var(--spacing-3);
                    border-bottom: 1px solid var(--colors-common-border-default);
                }
            }
        }

        &--buttons {
            display: flex;
            gap: var(--spacing-1);
        }
    }

    &__illustration {
        width: 539px;

        img {
            height: auto;
            max-width: 100%;
            object-fit: contain;
        }
    }
}
</style>
